import type { MediaMetadata } from '@/types/storage'
import { azureTtsService, type AzureTtsVoice } from '@/features/editor/services/azure-tts-service'
import {
  kokoroTtsService,
  type KokoroTtsModel,
  type KokoroTtsVoice,
} from '@/features/editor/services/kokoro-tts-service'
import { mediaLibraryService } from '@/features/media-library/services/media-library-service'
import { mossTtsService, type MossTtsVoice } from '@/features/editor/services/moss-tts-service'
import {
  supertonicTtsService,
  type SupertonicTtsLanguageSelection,
  type SupertonicTtsVoice,
} from '@/features/editor/services/supertonic-tts-service'

export type AssistantTtsEngine = 'moss' | 'kokoro' | 'azure' | 'supertonic'

type MossRequest = {
  engine: 'moss'
  text: string
  voice: MossTtsVoice
  speed?: number
  onProgress?: (stage: string) => void
}

type KokoroRequest = {
  engine: 'kokoro'
  text: string
  voice: KokoroTtsVoice
  model: KokoroTtsModel
  speed?: number
  onProgress?: (stage: string) => void
}

type AzureRequest = {
  engine: 'azure'
  text: string
  voice: AzureTtsVoice
  speed?: number
  onProgress?: (stage: string) => void
}

type SupertonicRequest = {
  engine: 'supertonic'
  text: string
  voice: SupertonicTtsVoice
  language: SupertonicTtsLanguageSelection
  speed?: number
  onProgress?: (stage: string) => void
}

export type GenerateTtsAudioRequest = MossRequest | KokoroRequest | AzureRequest | SupertonicRequest

export async function generateTtsAudio(request: GenerateTtsAudioRequest): Promise<{
  blob: Blob
  file: File
  duration: number
  engine: AssistantTtsEngine
}> {
  const speed = request.speed ?? 1

  switch (request.engine) {
    case 'moss': {
      const result = await mossTtsService.generateSpeechFile({
        text: request.text,
        voice: request.voice,
        speed,
        onProgress: request.onProgress,
      })
      return { ...result, engine: request.engine }
    }
    case 'kokoro': {
      const result = await kokoroTtsService.generateSpeechFile({
        text: request.text,
        voice: request.voice,
        model: request.model,
        speed,
        onProgress: request.onProgress,
      })
      return { ...result, engine: request.engine }
    }
    case 'azure': {
      const result = await azureTtsService.generateSpeechFile({
        text: request.text,
        voice: request.voice,
        speed,
        onProgress: request.onProgress,
      })
      return { ...result, engine: request.engine }
    }
    case 'supertonic': {
      const result = await supertonicTtsService.generateSpeechFile({
        text: request.text,
        voice: request.voice,
        language: request.language,
        speed,
        onProgress: request.onProgress,
      })
      return { ...result, engine: request.engine }
    }
  }
}

export async function saveGeneratedAudioToProjectMedia(
  file: File,
  projectId: string,
  options?: {
    tags?: string[]
    codec?: string
  },
): Promise<MediaMetadata> {
  return mediaLibraryService.importGeneratedAudio(file, projectId, {
    tags: options?.tags,
    codec: options?.codec,
  })
}
